(ns app.front.view.group.admin.templates.stamps.subs
  (:require [app.domain.stamp.model :as stamp-model]
            [app.front.file.upload :as upload]
            [app.front.view.group.admin.templates.stamps.builder :as builder]
            [clojure.set :as set]
            [core.front.event :refer [>evt]]
            [re-frame.core-instrumented :as rf]))

(defn builder-draft [context stamp-id]
  (if stamp-id
    (some #(when (= stamp-id (:id %)) %) (:stamps context))
    {:name "" :assets {} :layout stamp-model/default-layout}))

(defn save-request [group-id draft]
  (if (:id draft)
    {:command :stamp/update
     :data    (assoc (builder/persisted-state draft) :authority-group-id group-id
                     :stamp-id (:id draft) :version (:version draft))}
    {:command :stamp/create
     :data    (assoc (builder/persisted-state draft) :authority-group-id group-id)}))

(defn delete-request [group-id {:keys [id version]}]
  {:command :stamp/delete
   :data    {:authority-group-id group-id
             :stamp-id           id
             :version            version}})

(defn- pending? [state request-id]
  ;; Each load/write gets one ID. Clearing or replacing the page invalidates its callbacks,
  ;; including when the same stamp is reopened before an earlier request finishes.
  (and request-id (= request-id (:request-id state))))

(defn- upload-pending? [editor pending-files]
  (seq (get pending-files (:upload-component-id editor))))

(defn- editor-busy? [db]
  (or (:busy? (::editor db))
      (upload-pending? (::editor db) (::upload/pending-files db))))

(rf/reg-event-fx ::load-collection
  (fn [{:keys [db]} [_ group-id]]
    (let [request-id (random-uuid)
          failed     [::collection-failed request-id]]
      {:db (assoc db ::collection {:group-id group-id :loading? true :request-id request-id})
       :comm/command
       {:command    :stamp/list
        :method     :get
        :data       {:authority-group-id group-id}
        :on-success [::collection-loaded request-id]
        :on-done    failed
        :on-error   failed}})))

(rf/reg-event-db ::clear-collection
  (fn [db _] (dissoc db ::collection ::confirmation)))

(rf/reg-event-db ::collection-loaded
  (fn [db [_ request-id _status context]]
    (if (pending? (::collection db) request-id)
      (update db ::collection assoc :context context :loading? false :request-id nil)
      db)))

(rf/reg-event-db ::collection-failed
  (fn [db [_ request-id _status body]]
    (if (pending? (::collection db) request-id)
      (update db ::collection
              #(assoc % :loading? false :busy? false :request-id nil
                      :error (if (= :error/entity-version-not-match (:error body))
                               :stamp/version-conflict
                               (if (:loading? %) :stamp/load-error :stamp/save-error))))
      db)))

(rf/reg-event-fx ::load-editor
  (fn [{:keys [db]} [_ group-id stamp-id]]
    (let [request-id (random-uuid)
          failed     [::editor-failed request-id :stamp/load-error]]
      {:db (assoc db ::editor {:group-id            group-id
                               :stamp-id            stamp-id
                               :loading?            true
                               :request-id          request-id
                               :upload-component-id (str "stamp-image-" (random-uuid))})
       :comm/command
       {:command    :stamp/list
        :method     :get
        :data       {:authority-group-id group-id}
        :on-success [::editor-loaded request-id]
        :on-done    failed
        :on-error   failed}})))

(defn- cleanup-command [group-id image-ids]
  (when (seq image-ids)
    {:command  :stamp/discard-images
     :data     {:authority-group-id group-id :image-ids image-ids}
     :on-done  [:comm/noop]
     :on-error [:comm/noop]}))

(rf/reg-event-fx ::clear-editor
  (fn [{:keys [db]} _]
    (let [{:keys [group-id uploaded-image-ids submitted-image-ids]} (::editor db)]
      {:db (dissoc db ::editor ::confirmation)
       :fx (if-let [command (cleanup-command group-id (set/difference uploaded-image-ids submitted-image-ids))]
             [[:comm/command command]]
             [])})))

(rf/reg-event-db ::editor-loaded
  (fn [db [_ request-id _status context]]
    (if (pending? (::editor db) request-id)
      (let [draft (builder-draft context (:stamp-id (::editor db)))]
        (update db ::editor assoc
                :context context :loading? false :request-id nil
                :draft draft
                :baseline (when draft (builder/persisted-state draft))
                :missing? (nil? draft)
                :invalid? (and draft (not (stamp-model/valid-layout? (:layout draft))))))
      db)))

(rf/reg-event-db ::editor-failed
  (fn [db [_ request-id error-key _status body]]
    (if (pending? (::editor db) request-id)
      (update db ::editor assoc
              :loading? false :busy? false :request-id nil
              :error (case (:error body)
                       :stamp/invalid-image :stamp/invalid-image
                       :error/entity-version-not-match :stamp/version-conflict
                       error-key))
      db)))

(rf/reg-event-db ::change-draft
  (fn [db [_ draft]]
    (if (and (::editor db) (not (editor-busy? db)))
      (assoc-in db [::editor :draft] draft)
      db)))

(rf/reg-event-fx ::save
  (fn [{:keys [db]} [_ return-route]]
    (let [{:keys [draft group-id loading?]} (::editor db)]
      (when (and draft (not (editor-busy? db)) (not loading?))
        (let [request-id (random-uuid)
              failed     [::editor-failed request-id :stamp/save-error]]
          {:db (update db ::editor
                       #(-> %
                            (assoc :busy? true :request-id request-id :error nil)
                            ;; A failed/lost response does not prove that the server did not save these uploads.
                            (update :submitted-image-ids (fnil into #{})
                                    (vals (:assets (builder/persisted-state draft))))))
           :comm/command
           (assoc (save-request group-id draft)
                  :on-success [::saved request-id return-route]
                  :on-done failed :on-error failed)})))))

(rf/reg-event-fx ::saved
  (fn [{:keys [db]} [_ request-id return-route]]
    (when (pending? (::editor db) request-id)
      (let [{:keys [group-id uploaded-image-ids submitted-image-ids draft]} (::editor db)
            persisted                                                       (builder/persisted-state draft)
            unused                                                          (set/difference uploaded-image-ids submitted-image-ids)]
        {:db         (update db ::editor
                             #(assoc % :busy? false :request-id nil :uploaded-image-ids #{}
                                     :baseline persisted))
         :fx         (if-let [command (cleanup-command group-id unused)]
                       [[:comm/command command]]
                       [])
         :route/push return-route}))))

(rf/reg-event-db ::close-confirmation
  (fn [db _]
    ;; Keep the outgoing content throughout the modal's closing transition.
    (if (::confirmation db)
      (assoc-in db [::confirmation :open?] false)
      db)))

(rf/reg-sub ::confirmation :-> ::confirmation)

(rf/reg-event-fx ::request-leave
  (fn [{:keys [db]} [_ return-route message]]
    (let [editor (::editor db)]
      (when (and editor (not (editor-busy? db)))
        (if (builder/dirty? editor)
          {:db (assoc db ::confirmation {:open? true :message message :on-confirm [::leave-confirmed return-route]})}
          {:fx [[:dispatch [::leave-confirmed return-route]]]})))))

(rf/reg-event-fx ::leave-confirmed
  (fn [{:keys [db]} [_ return-route]]
    (when (and (::editor db) (not (editor-busy? db)))
      {:db         (update db ::editor #(assoc % :baseline (builder/persisted-state (:draft %))))
       :route/push return-route})))

(rf/reg-fx ::prevent-unload
  (fn [event]
    (.preventDefault event)
    (set! (.-returnValue event) "")))

(rf/reg-event-fx ::before-unload
  (fn [{:keys [db]} [_ event]]
    (when (builder/dirty? (::editor db))
      {::prevent-unload event})))

(rf/reg-event-db ::request-delete
  (fn [db [_ stamp message]]
    (if (and (::collection db) (not (:busy? (::collection db))))
      (assoc db ::confirmation {:open? true :message message :on-confirm [::delete-confirmed stamp]})
      db)))

(rf/reg-event-fx ::delete-confirmed
  (fn [{:keys [db]} [_ stamp]]
    (when (and (::collection db) (not (:busy? (::collection db))))
      (let [request-id (random-uuid)
            failed     [::collection-failed request-id]]
        {:db (update db ::collection assoc :busy? true :request-id request-id :error nil)
         :comm/command
         (assoc (delete-request (:group-id (::collection db)) stamp)
                :on-success [::deleted request-id]
                :on-done failed :on-error failed)}))))

(rf/reg-event-fx ::deleted
  (fn [{:keys [db]} [_ request-id]]
    (when (pending? (::collection db) request-id)
      {:fx [[:dispatch [::load-collection (:group-id (::collection db))]]]})))

(rf/reg-event-fx ::upload
  (fn [{:keys [db]} [_ {:keys [file file-hash component-id]}]]
    (let [{:keys [draft upload-component-id busy?]} (::editor db)]
      (when (and draft (= component-id upload-component-id) (not busy?))
        (let [request-id (random-uuid)
              failed     [::upload/upload-failure {:md5 file-hash :component-id component-id}]
              stored!    (fn [{:keys [file-id]}]
                           (>evt [::image-put request-id file file-id]))]
          {:db (update db ::editor assoc :request-id request-id :error nil)
           ;; Reading is complete. Replace the picker's placeholder with the upload's hash
           ;; so the shared failure handler can also finish its loading state on init failure.
           :fx [[:dispatch [::upload/add-pending-file component-id file-hash]]
                [:dispatch [::upload/remove-pending-file component-id :placeholder]]]
           :comm/command
           {:command    :attachment/initialize-upload
            :data       {:content-type (upload/file-type file)
                         :md5-digest   file-hash}
            :on-success [::upload/start-file-upload file file-hash stored! component-id nil nil]
            :on-done    failed
            :on-error   failed}})))))

(rf/reg-event-fx ::image-put
  (fn [{:keys [db]} [_ request-id file file-id]]
    (when (pending? (::editor db) request-id)
      (let [failed [::editor-failed request-id :stamp/upload-error]]
        {:db (update db ::editor assoc :busy? true)
         :comm/command
         {:command    :stamp/upload-image
          :data       {:authority-group-id (:group-id (::editor db)) :file-id file-id}
          :on-success [::image-stored request-id file]
          :on-done    failed
          :on-error   failed}}))))

(rf/reg-fx ::image-dimensions
  (fn [{:keys [file image request-id]}]
    (-> (js/createImageBitmap file)
        (.then (fn [bitmap]
                 (try (>evt [::image-ready request-id image (.-width bitmap) (.-height bitmap)])
                      (finally (.close bitmap)))))
        (.catch #(>evt [::editor-failed request-id :stamp/upload-error])))))

(rf/reg-event-fx ::image-stored
  (fn [{:keys [db]} [_ request-id file _status image]]
    (when (pending? (::editor db) request-id)
      {:db                (update-in db [::editor :uploaded-image-ids] (fnil conj #{}) (:image-id image))
       ::image-dimensions {:file file :image image :request-id request-id}})))

(rf/reg-event-db ::image-ready
  (fn [db [_ request-id image width height]]
    (if (pending? (::editor db) request-id)
      (update db ::editor
              #(-> % (update :draft builder/add-image image width height)
                   (assoc :busy? false :request-id nil)))
      db)))

(rf/reg-sub ::editor-state :-> ::editor)
(rf/reg-sub ::pending-files :-> ::upload/pending-files)

(rf/reg-sub ::editor
  :<- [::editor-state]
  :<- [::pending-files]
  (fn [[state pending-files] _]
    (if state
      (assoc state :dirty? (builder/dirty? state)
             :busy? (boolean (or (:busy? state) (upload-pending? state pending-files)))
             :image-urls (stamp-model/image-urls (:group-id state) (get-in state [:draft :assets])))
      {:loading? true})))

(rf/reg-sub ::collection :-> #(or (::collection %) {:loading? true}))
